import React, { useEffect, useRef, useState } from 'react';

/**
 * Silent looping product clip laid over the product photo, so the
 * catalogue moves on its own. The clip plays whenever its card is on
 * screen, on every device, and pauses when it scrolls away so a long grid
 * never decodes more than the clips people can see. Nothing downloads
 * until the card comes near the viewport. The photo stays underneath as
 * the poster, and the clip only fades in once it is actually playing.
 *
 * `active={false}` holds a clip back (the hero's slides that aren't on
 * stage): it downloads nothing until its first turn and pauses between.
 *
 * iPhones only autoplay a clip whose `muted` attribute is set (React sets
 * the property only), and refuse autoplay in Low Power Mode until the
 * visitor touches the page, so a refused clip tries again on first touch.
 *
 * Visitors who ask for reduced motion, or who have Data Saver on, keep the
 * still photo.
 */
const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
const saveData = () => typeof navigator !== 'undefined' && navigator.connection?.saveData === true;

const ProductAnimation = ({ src, poster, hostRef, active = true, className = '' }) => {
  const videoRef = useRef(null);
  const [near, setNear] = useState(false);
  const [visible, setVisible] = useState(false);
  const [armed, setArmed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const disabled = !src || reducedMotion() || saveData();

  useEffect(() => {
    const host = hostRef?.current;
    if (disabled || !host || typeof IntersectionObserver === 'undefined') return undefined;
    // Two observers: one starts the download a little before the card
    // arrives, the other plays only while enough of it is showing.
    const approach = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setNear(true); approach.disconnect(); }
    }, { rootMargin: '300px 0px' });
    const view = new IntersectionObserver(([entry]) => setVisible(entry.intersectionRatio >= 0.35), { threshold: [0, 0.35, 0.7] });
    approach.observe(host);
    view.observe(host);
    return () => { approach.disconnect(); view.disconnect(); };
  }, [disabled, hostRef]);

  useEffect(() => {
    if (near && active) setArmed(true);
  }, [near, active]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    if (!(visible && armed && active)) {
      video.pause();
      return undefined;
    }
    video.defaultMuted = true; // writes the muted attribute iOS checks
    video.muted = true;
    let retry = null;
    let cancelled = false;
    const played = video.play();
    if (played && typeof played.catch === 'function') {
      played.catch(() => {
        if (cancelled) return;
        retry = () => {
          forget();
          const again = video.play();
          if (again?.catch) again.catch(() => {});
        };
        document.addEventListener('touchstart', retry, { passive: true });
        document.addEventListener('pointerdown', retry);
      });
    }
    const forget = () => {
      if (!retry) return;
      document.removeEventListener('touchstart', retry);
      document.removeEventListener('pointerdown', retry);
    };
    return () => { cancelled = true; forget(); };
  }, [visible, armed, active]);

  if (disabled) return null;
  return (
    <video
      ref={videoRef}
      className={`product-animation${playing ? ' is-playing' : ''} ${className}`}
      src={armed ? src : undefined}
      poster={poster}
      muted
      loop
      playsInline
      preload={armed ? 'auto' : 'none'}
      aria-hidden="true"
      tabIndex={-1}
      onPlaying={() => setPlaying(true)}
    />
  );
};

export default ProductAnimation;
